/**
 * Browser-side error logger. Mirrors legacy `amri.log.js`.
 *
 *   - window.onerror → POST `{ level: 'ERROR', message, source, line, column, url, stack }`
 *     to /umbraco/surface/logsurface/logclientsidemessage.
 *   - window.onunhandledrejection → same shape, Stack from reason.
 *
 * Failures to log are swallowed (the page shouldn't crash because the
 * logging pipeline is down).
 */

const ENDPOINT = '/umbraco/surface/logsurface/logclientsidemessage';

interface LogPayload {
    level: 'ERROR' | 'WARN' | 'INFO';
    message: string;
    source?: string;
    line?: number;
    column?: number;
    url: string;
    stack?: string;
}

function postPayload(payload: LogPayload): void {
    try {
        // Beacon: best-effort, no response needed, no fetch retry.
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        if (navigator.sendBeacon) {
            navigator.sendBeacon(ENDPOINT, blob);

            return;
        }

        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {
            // Swallow — logging must never crash the page.
        });
    } catch {
        // Swallow — logging must never crash the page.
    }
}

function handleError(e: ErrorEvent): void {
    postPayload({
        level: 'ERROR',
        message: e.message ?? 'Unknown error',
        source: e.filename,
        line: e.lineno,
        column: e.colno,
        url: window.location.href,
        stack: e.error?.stack,
    });
}

function handleRejection(e: PromiseRejectionEvent): void {
    const reason = e.reason as { message?: string; stack?: string } | string | undefined;
    const message = typeof reason === 'string' ? reason : (reason?.message ?? 'Unhandled promise rejection');
    const stack = typeof reason === 'object' && reason !== null ? reason.stack : undefined;

    const payload: LogPayload = {
        level: 'ERROR',
        message,
        url: window.location.href,
    };
    if (stack) {
        payload.stack = stack;
    }

    postPayload(payload);
}

export function initClientLog(): void {
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
}
